// src/common/profile/ProfileMenu.js
import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { setGhostUser } from "../../../redux/actions/UserProfileActions";
import Logout from "../signIn/Logout";
import { Dropdown, Modal, Button, Alert } from "react-bootstrap";
import EmployeeSearchAdmin from "../empsearch/EmployeeSearchAdmin";
import SecureImpersonationService from "../../../services/SecureImpersonationService";

const ProfileMenu = ({ profile, setGhostUser, logout }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showImpersonationModal, setShowImpersonationModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isStartingImpersonation, setIsStartingImpersonation] = useState(false);
  const [isStoppingImpersonation, setIsStoppingImpersonation] = useState(false);
  const [error, setError] = useState(null);
  const [impersonationStatus, setImpersonationStatus] = useState({ isImpersonating: false });

  // Load impersonation status on component mount
  useEffect(() => {
    loadImpersonationStatus();
  }, []);

  const loadImpersonationStatus = async () => {
    try {
      const status = await SecureImpersonationService.getImpersonationStatus();
      setImpersonationStatus(status);
    } catch (err) {
      console.error("Error loading impersonation status:", err);
    }
  };

  const handleStartImpersonation = async () => {
    if (!selectedEmployee) return;
    
    setIsStartingImpersonation(true);
    setError(null);
    
    try {
      await SecureImpersonationService.startImpersonation(selectedEmployee.employeeId);
      setShowImpersonationModal(false);
      
      // Update the impersonation status immediately
      await loadImpersonationStatus();
      
      // Optionally refresh the page, or just rely on the state update
      window.location.reload(); // Refresh to apply new context
    } catch (err) {
      setError(err.message || "Failed to start impersonation");
    } finally {
      setIsStartingImpersonation(false);
    }
  };

  const handleStopImpersonation = async () => {
    setIsStoppingImpersonation(true);
    setError(null);
    
    try {
      await SecureImpersonationService.stopImpersonation();
      window.location.reload(); // Refresh to restore original context
    } catch (err) {
      setError(err.message || "Failed to stop impersonation");
    } finally {
      setIsStoppingImpersonation(false);
    }
  };

  // Toggle dropdown visibility
  const handleToggleDropdown = (isOpen) => {
    setShowDropdown(isOpen);
  };
  
  return (
    <div className="d-flex align-items-center">
      {/* Impersonation Status Banner */}
      {impersonationStatus.isImpersonating && (
        <div className="impersonation-banner bg-warning text-dark px-3 py-1 me-2 rounded d-flex align-items-center">
          <i className="fas fa-user-secret me-2"></i>
          <span>Viewing as: <strong>{impersonationStatus.impersonatedUser?.name}</strong></span>
          <Button 
            variant="outline-dark" 
            size="sm" 
            className="ms-2"
            onClick={handleStopImpersonation}
            disabled={isStoppingImpersonation}
          >
            {isStoppingImpersonation ? "Exiting..." : "Exit"}
          </Button>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" className="me-2 mb-0 py-2 px-3" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Dropdown Menu */}
      <Dropdown alignRight show={showDropdown} onToggle={handleToggleDropdown}>
        <Dropdown.Toggle variant="primary">
          {profile.name}{" "}
        </Dropdown.Toggle>
        <Dropdown.Menu className="profile-menu">
          <Dropdown.Header className="profile-menu-title">
            <strong>Welcome {profile.name}</strong>
            {impersonationStatus.isImpersonating && (
              <div className="text-warning mt-1">
                <small><i className="fas fa-user-secret"></i> Admin View Active</small>
              </div>
            )}
          </Dropdown.Header>
          <Dropdown.Divider />
          
          {/* Admin Impersonation Option */}
          {profile.isAdmin && !impersonationStatus.isImpersonating && (
            <>
              <Dropdown.Item onClick={() => setShowImpersonationModal(true)}>
                <i className="fas fa-user-secret me-2"></i> Impersonate User
              </Dropdown.Item>
              <Dropdown.Divider />
            </>
          )}
          
          <Dropdown.Header>
            <Logout logout={logout} />
          </Dropdown.Header>
        </Dropdown.Menu>
      </Dropdown>

      {/* Impersonation Modal */}
      <Modal show={showImpersonationModal} onHide={() => setShowImpersonationModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-user-secret me-2"></i>
            Impersonate User
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          
          <Alert variant="info">
            <strong>Admin Impersonation</strong><br/>
            Select an employee to view the application exactly as they would see it. 
            All actions will be performed as the selected user.
          </Alert>
          
          <div className="mb-3">
            <label className="form-label"><strong>Select Employee:</strong></label>
            <EmployeeSearchAdmin 
              onSelect={setSelectedEmployee}
              useRsEmployeeId={false} // Use EP Employee ID for impersonation
            />
          </div>
          
          {selectedEmployee && (
            <Alert variant="success" className="mt-3">
              <strong>Selected:</strong> {selectedEmployee.firstName} {selectedEmployee.lastName} ({selectedEmployee.employeeNumber})<br/>
              <small>Employee ID: {selectedEmployee.employeeId}</small>
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowImpersonationModal(false)}
            disabled={isStartingImpersonation}
          >
            Cancel
          </Button>
          <Button 
            variant="warning" 
            onClick={handleStartImpersonation}
            disabled={!selectedEmployee || isStartingImpersonation}
          >
            {isStartingImpersonation ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Starting...
              </>
            ) : (
              <>
                <i className="fas fa-user-secret me-2"></i>
                Start Impersonation
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
};

const mapStateToProps = (state) => ({
  profile: state.profile
});

const mapDispatchToProps = {
  setGhostUser
};

export default connect(mapStateToProps, mapDispatchToProps)(ProfileMenu);